import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FC, useCallback, useEffect, useState } from "react";
import { useTaxonomyContext } from "../../../contexts/Taxonomy/TaxonomyContext";
import { formatCountLabel } from "../../../utils/text";
import { FilterChip } from "../FilterChip";
import TreeSelect from "../TreeSelect/TreeSelect";
import { transformToTree } from "../TreeSelect/utils";

interface Props {
  selectedItems: string[];
  onSelectedItems: (selectedItems: string[]) => void;
  highlighted?: boolean;
  id?: string;
}

export const SpendCategoryFilterChip: FC<Props> = ({
  selectedItems,
  onSelectedItems,
  highlighted,
  id = "spend-category",
}) => {
  const { spendCategories } = useTaxonomyContext();
  const [tempSelectedSpendCategories, setTempSelectedSpendCategories] =
    useState<string[]>(selectedItems);
  const [selectedSpendCategoryCount, setSelectedSpendCategoryCount] = useState(
    selectedItems.length
  );

  useEffect(() => {
    setTempSelectedSpendCategories(selectedItems);
    setSelectedSpendCategoryCount(selectedItems.length);
  }, [selectedItems]);

  const applyFilterChanges = useCallback(
    (itemsToApply: string[], countToApply: number) => {
      onSelectedItems(itemsToApply);
      setSelectedSpendCategoryCount(countToApply);
    },
    [onSelectedItems]
  );

  const handleTreeSelectChange = useCallback(
    (newSelectedTreeItems: string[]) => {
      setTempSelectedSpendCategories(newSelectedTreeItems);
    },
    []
  );

  const options =
    transformToTree(spendCategories, (category) => category.name)[0]
      ?.children || [];

  return (
    <FilterChip
      id={id}
      label="Spend Category"
      getSelectedCount={() => selectedSpendCategoryCount}
      highlighted={highlighted}
      popoverOptions={{
        disableActionButtons: true,
        //** Selected IT Spend Categories will be applied when user unfocus's from the input
        onClose: () => {
          applyFilterChanges(
            tempSelectedSpendCategories,
            selectedSpendCategoryCount
          );
        },
      }}
      renderLabel={(label, count) => (
        <Typography component="span" color="textSecondary">
          {formatCountLabel(label, count)}
        </Typography>
      )}
    >
      <Stack direction="column" gap={1}>
        <TreeSelect
          items={options}
          selectedItems={tempSelectedSpendCategories}
          onSelectItems={handleTreeSelectChange}
          placeholder={"Search Spend Categories"}
          noOptionsMessage={"No Results"}
        />

        <Stack alignSelf="start">
          <Button
            variant="text"
            color="inherit"
            size="small"
            onClick={() => handleTreeSelectChange([])}
          >
            clear all entries
          </Button>
        </Stack>
      </Stack>
    </FilterChip>
  );
};
